/**
 * usePermissions Hook
 * 
 * Provides permission checking utilities for components.
 * Uses the central permissions configuration and auth context.
 */

import { useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  ROLES,
  PERMISSIONS,
  hasPermission as checkPermission,
  canAccessRoute,
  isAdminRole,
  isUserRole,
  isDeveloperRole,
  getDashboardType,
  getAdminPageType,
  getNavigationItems,
} from '../config/permissions';

/**
 * Hook for checking user permissions
 * @returns Permission checking utilities
 */
export function usePermissions() {
  const { user } = useAuth();
  
  // Get the user's role, defaulting to 'user' if not set
  const role = useMemo(() => {
    return user?.role?.toLowerCase() || ROLES.USER;
  }, [user?.role]);

  // Check if user is a developer
  const isDeveloper = useMemo(() => {
    return user?.isDeveloper === true;
  }, [user?.isDeveloper]);

  // Check if user has a specific permission
  // DEVELOPERS: Always return true (god-mode access)
  // First checks database permissions, then falls back to hardcoded matrix
  const hasPermission = useCallback((permission) => {
    if (isDeveloper) return true;
    
    // Check database permissions first (from user.permissions object)
    if (user?.permissions && typeof user.permissions === 'object') {
      if (permission in user.permissions) {
        return user.permissions[permission] === true;
      }
    }
    
    // Fallback to hardcoded permission matrix
    return checkPermission(role, permission);
  }, [role, isDeveloper, user]);

  // Check if user can access a specific route
  // DEVELOPERS: Always return true (god-mode access)
  const canAccess = useCallback((route) => {
    if (isDeveloper) return true;
    return canAccessRoute(role, route);
  }, [role, isDeveloper]);

  // Role type checks
  const isAdmin = useMemo(() => isAdminRole(role), [role]);
  const isUser = useMemo(() => isUserRole(role), [role]);
  // Note: isDeveloper is already declared above (checks user?.isDeveloper flag)

  // Dashboard type for this user
  const dashboardType = useMemo(() => getDashboardType(role), [role]);

  // Admin page type for this user
  const adminPageType = useMemo(() => getAdminPageType(role), [role]);

  // Navigation items for this user
  const navigationItems = useMemo(() => getNavigationItems(role), [role]);

  // Commonly used permission checks
  // DEVELOPERS: All permissions return true
  const permissions = useMemo(() => {
    if (isDeveloper) {
      return {
        // Page access
        canViewDashboard: true,
        canViewRepairs: true,
        canViewMap: true,
        canViewAssets: true,
        canViewAdmin: true,
        canViewSettings: true,
        
        // Organization management
        canManageAllOrganizations: true,
        canManageOwnOrganizations: true,
        canAddOrganization: true,
        
        // User management
        canViewUsers: true,
        canManageUsers: true,
        
        // File management
        canViewFiles: true,
        canManageFiles: true,
        
        // Repair management
        canViewAllRepairs: true,
        canViewOwnRepairs: true,
        canCreateRepair: true,
        canManageRepairs: true,
        
        // Admin features
        canAccessAdminReporting: true,
        canAccessSystemSettings: true,
      };
    }
    
    return {
      // Page access
      canViewDashboard: checkPermission(role, PERMISSIONS.VIEW_DASHBOARD),
      canViewRepairs: checkPermission(role, PERMISSIONS.VIEW_REPAIRS),
      canViewMap: checkPermission(role, PERMISSIONS.VIEW_MAP),
      canViewAssets: checkPermission(role, PERMISSIONS.VIEW_ASSETS),
      canViewAdmin: checkPermission(role, PERMISSIONS.VIEW_ADMIN),
      canViewSettings: checkPermission(role, PERMISSIONS.VIEW_SETTINGS),
      
      // Organization management
      canManageAllOrganizations: checkPermission(role, PERMISSIONS.MANAGE_ALL_ORGANIZATIONS),
      canManageOwnOrganizations: checkPermission(role, PERMISSIONS.MANAGE_OWN_ORGANIZATIONS),
      canAddOrganization: checkPermission(role, PERMISSIONS.ADD_ORGANIZATION),
      
      // User management
      canViewUsers: checkPermission(role, PERMISSIONS.VIEW_USERS),
      canManageUsers: checkPermission(role, PERMISSIONS.MANAGE_USERS),
      
      // File management
      canViewFiles: checkPermission(role, PERMISSIONS.VIEW_FILES),
      canManageFiles: checkPermission(role, PERMISSIONS.MANAGE_FILES),
      
      // Repair management
      canViewAllRepairs: checkPermission(role, PERMISSIONS.VIEW_ALL_REPAIRS),
      canViewOwnRepairs: checkPermission(role, PERMISSIONS.VIEW_OWN_REPAIRS),
      canCreateRepair: checkPermission(role, PERMISSIONS.CREATE_REPAIR),
      canManageRepairs: checkPermission(role, PERMISSIONS.MANAGE_REPAIRS),
      
      // Admin features
      canAccessAdminReporting: checkPermission(role, PERMISSIONS.ADMIN_REPORTING),
      canAccessSystemSettings: checkPermission(role, PERMISSIONS.SYSTEM_SETTINGS),
    };
  }, [role, isDeveloper]);

  return {
    // User info
    role,
    user,
    isDeveloper,
    
    // Permission checking functions
    hasPermission,
    canAccess,
    
    // Role type checks
    isAdmin: isDeveloper || isAdmin, // Developers are also admins
    isUser,
    isDeveloper,
    
    // Page types
    dashboardType,
    adminPageType,
    
    // Navigation
    navigationItems,
    
    // Pre-computed permissions
    permissions,
    
    // Export constants for component use
    ROLES,
    PERMISSIONS,
  };
}

/**
 * CanView Component
 * Conditionally renders children based on permission
 * 
 * Usage:
 * <CanView permission={PERMISSIONS.VIEW_USERS}>
 *   <UserList />
 * </CanView>
 */
export function CanView({ permission, fallback = null, children }) {
  const { hasPermission } = usePermissions();
  
  if (hasPermission(permission)) {
    return children;
  }
  
  return fallback;
}

/**
 * CanAccess Component
 * Conditionally renders children based on route access
 * 
 * Usage:
 * <CanAccess route="/admin">
 *   <AdminLink />
 * </CanAccess>
 */
export function CanAccess({ route, fallback = null, children }) {
  const { canAccess } = usePermissions();
  
  if (canAccess(route)) {
    return children;
  }
  
  return fallback;
}

/**
 * RoleGuard Component
 * Renders children only for specific roles
 * 
 * Usage:
 * <RoleGuard roles={['admin', 'manager']}>
 *   <AdminControls />
 * </RoleGuard>
 */
export function RoleGuard({ roles, fallback = null, children }) {
  const { role } = usePermissions();
  
  if (roles.includes(role)) {
    return children;
  }
  
  return fallback;
}

/**
 * AdminOnly Component
 * Renders children only for admin/manager roles
 */
export function AdminOnly({ fallback = null, children }) {
  const { isAdmin } = usePermissions();
  return isAdmin ? children : fallback;
}

/**
 * UserOnly Component
 * Renders children only for user roles
 */
export function UserOnly({ fallback = null, children }) {
  const { isUser } = usePermissions();
  return isUser ? children : fallback;
}

/**
 * CustomerOnly Component (deprecated - use UserOnly instead)
 * Renders children only for user roles
 * @deprecated Use UserOnly instead
 */
export function CustomerOnly({ fallback = null, children }) {
  const { isUser } = usePermissions();
  return isUser ? children : fallback;
}

/**
 * VendorOnly Component (deprecated - vendors are now users)
 * Renders children only for user roles
 * @deprecated Use UserOnly instead
 */
export function VendorOnly({ fallback = null, children }) {
  const { isUser } = usePermissions();
  return isUser ? children : fallback;
}

/**
 * NotVendor Component (deprecated - vendors are now users)
 * Renders children for all roles
 * @deprecated This component always renders children now
 */
export function NotVendor({ fallback = null, children }) {
  return children;
}

export default usePermissions;

